import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import GamesForm from './GamesForm';
import Modal from './Modal';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { uploadPoster } from '../utils/imageUpload';
import { downloadCardImage } from '../utils/downloadCard';
import { tagAtPatchOnEdit, byTagAtOrCreatedAt } from '../utils/tagTimestamps';
import { availablePeople } from '../utils/people';
import { useLongPress } from '../hooks/useLongPress';
import { useAuth } from '../AuthContext';

const PLAYED_AT_FIELD = {
  Joy: 'played_with_joy_at',
  Rick: 'played_with_rick_at',
  Lisa: 'played_with_lisa_at',
  Gionnan: 'played_with_gionnan_at',
  Youssra: 'played_with_youssra_at',
};

const PLAYED_WITH_FIELD = {
  Joy: 'played_with_joy',
  Rick: 'played_with_rick',
  Lisa: 'played_with_lisa',
  Gionnan: 'played_with_gionnan',
  Youssra: 'played_with_youssra',
};

const fetchAllGames = async () => {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/games?order=created_at.desc&select=*&limit=1000`, {
    headers: supabaseHeaders(false),
  });
  if (!res.ok) throw new Error('Failed to fetch games');
  return res.json();
};

function App() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [originalGames, setOriginalGames] = useState([]);
  const [games, setGames] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [watchedWithFilter, setWatchedWithFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('original');
  const [showAddForm, setShowAddForm] = useState(false);

  const cardRefs = useRef([]);
  const downloadLongPress = useLongPress();
  const editLongPress = useLongPress();

  const [editingGame, setEditingGame] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editPoster, setEditPoster] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editStatus, setEditStatus] = useState('');

  const openEdit = (game) => {
    setEditingGame(game);
    setEditForm({
      title: game.title || '',
      rating: game.rating || '',
      thoughts: game.thoughts || '',
      playedWithJoy: game.played_with_joy || false,
      playedWithRick: game.played_with_rick || false,
      playedWithLisa: game.played_with_lisa || false,
      playedWithGionnan: game.played_with_gionnan || false,
      playedWithYoussra: game.played_with_youssra || false,
    });
    setEditPoster(null);
    setEditStatus('');
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editForm.title || !editForm.rating) { setEditStatus('Please fill in a title and rating.'); return; }
    setEditLoading(true);
    setEditStatus('');
    try {
      let newPosterUrl = null;
      if (editPoster) newPosterUrl = await uploadPoster(editPoster);

      const tagAtPatch = tagAtPatchOnEdit([
        { wasOn: !!editingGame.played_with_joy, isOn: editForm.playedWithJoy, atField: 'played_with_joy_at' },
        { wasOn: !!editingGame.played_with_rick, isOn: editForm.playedWithRick, atField: 'played_with_rick_at' },
        { wasOn: !!editingGame.played_with_lisa, isOn: editForm.playedWithLisa, atField: 'played_with_lisa_at' },
        { wasOn: !!editingGame.played_with_gionnan, isOn: editForm.playedWithGionnan, atField: 'played_with_gionnan_at' },
        { wasOn: !!editingGame.played_with_youssra, isOn: editForm.playedWithYoussra, atField: 'played_with_youssra_at' },
      ]);

      const patch = {
        title: editForm.title,
        rating: parseFloat(editForm.rating),
        played_with_joy: editForm.playedWithJoy,
        played_with_rick: editForm.playedWithRick,
        played_with_lisa: editForm.playedWithLisa,
        played_with_gionnan: editForm.playedWithGionnan,
        played_with_youssra: editForm.playedWithYoussra,
        thoughts: editForm.thoughts || '',
        ...tagAtPatch,
      };
      if (newPosterUrl) patch.poster_url = newPosterUrl;

      const res = await fetch(`${SUPABASE_URL}/rest/v1/games?id=eq.${editingGame.id}`, {
        method: 'PATCH',
        headers: supabaseHeaders(true),
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error('Update failed');

      const updated = {
        ...editingGame,
        title: editForm.title,
        rating: parseFloat(editForm.rating),
        thoughts: editForm.thoughts || '',
        played_with_joy: editForm.playedWithJoy,
        played_with_rick: editForm.playedWithRick,
        played_with_lisa: editForm.playedWithLisa,
        played_with_gionnan: editForm.playedWithGionnan,
        played_with_youssra: editForm.playedWithYoussra,
        ...tagAtPatch,
        ...(newPosterUrl ? { poster_url: newPosterUrl } : {}),
      };
      const updateList = (list) => list.map((g) => g.id === editingGame.id ? updated : g);
      setOriginalGames(updateList);
      setGames(updateList);

      setEditStatus('Saved :D');
      setTimeout(() => setEditingGame(null), 1000);
    } catch (err) {
      console.error(err);
      setEditStatus(`Something went wrong: ${err.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${editingGame.title}"?`)) return;
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/games?id=eq.${editingGame.id}`, {
        method: 'DELETE',
        headers: supabaseHeaders(true),
      });
      if (!res.ok) throw new Error('Delete failed');
      setOriginalGames((prev) => prev.filter((g) => g.id !== editingGame.id));
      setGames((prev) => prev.filter((g) => g.id !== editingGame.id));
      setEditingGame(null);
    } catch (err) {
      setEditStatus(`Something went wrong: ${err.message}`);
    }
  };

  const fetchGames = useCallback(async () => {
    try {
      const all = await fetchAllGames();
      setOriginalGames(all);
      setGames(all);
    } catch (err) {
      console.error('Error fetching games:', err);
    }
  }, []);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  const sortGames = (order) => {
    setSortOrder(order);
    let sorted = [...originalGames];

    switch (order) {
      case 'highest':
        sorted.sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating));
        break;
      case 'lowest':
        sorted.sort((a, b) => parseFloat(a.rating) - parseFloat(b.rating));
        break;
      case 'original':
      default:
        sorted = [...originalGames];
        break;
    }

    applyFilters(sorted, ratingFilter, searchTerm, dateFilter, watchedWithFilter, order);
  };

  const filterByRatingRange = (value) => {
    setRatingFilter(value);
    applyFilters(originalGames, value, searchTerm, dateFilter, watchedWithFilter, sortOrder);
  };

  const filterByDateRange = (value) => {
    setDateFilter(value);
    applyFilters(originalGames, ratingFilter, searchTerm, value, watchedWithFilter, sortOrder);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    applyFilters(originalGames, ratingFilter, value, dateFilter, watchedWithFilter, sortOrder);
  };

  const filterByPerson = (value) => {
    setWatchedWithFilter(value);
    applyFilters(originalGames, ratingFilter, searchTerm, dateFilter, value, sortOrder);
  };

  const personOptions = availablePeople(originalGames, (value) => PLAYED_WITH_FIELD[value]);

  const applyFilters = (baseList, ratingValue, search, dateValue, watchedWith, sortOrderValue) => {
    let filtered = [...baseList];

    if (ratingValue) {
      const min = parseFloat(ratingValue);
      const max = min + 0.9;
      filtered = filtered.filter(
        (game) => game.rating >= min && game.rating <= max
      );
    }

    if (search) {
      filtered = filtered.filter((game) =>
        game.title.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (dateValue) {
      const now = new Date();
      const pastDate = new Date(now);
      pastDate.setDate(now.getDate() - parseInt(dateValue));
      filtered = filtered.filter(
        (game) => new Date(game.created_at) >= pastDate
      );
    }

    if (watchedWith && PLAYED_WITH_FIELD[watchedWith]) {
      const field = PLAYED_WITH_FIELD[watchedWith];
      filtered = filtered.filter((game) => game[field] === true);
    }

    if ((sortOrderValue || 'original') === 'original') {
      filtered.sort(byTagAtOrCreatedAt(watchedWith, PLAYED_AT_FIELD));
    }

    setGames(filtered);
  };

  return (
    <div className="app-container">
      <div className="header">
        <div className="header-title-row">
          <h1>Game Reviews</h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <button onClick={() => navigate('/todo-games')} style={{ fontFamily: 'angelWish', fontSize: '4rem', letterSpacing: '0.06em', background: 'white', border: '2px solid black', borderRadius: '8px', padding: '0.2em 0.6em', cursor: 'pointer' }}>To-Do</button>
            <button onClick={() => navigate('/goty')} style={{ fontFamily: 'angelWish', fontSize: '4rem', letterSpacing: '0.06em', background: 'white', border: '2px solid black', borderRadius: '8px', padding: '0.2em 0.6em', cursor: 'pointer' }}>GOTY</button>
          </div>
        </div>
        <div className="controls">
          {user && <button onClick={() => setShowAddForm(true)}>Add +</button>}
          <input
            type="text"
            placeholder="Search by title..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <button onClick={() => sortGames('highest')} className={sortOrder === 'highest' ? 'btn-active' : ''}>Highest Rating</button>
          <button onClick={() => sortGames('lowest')} className={sortOrder === 'lowest' ? 'btn-active' : ''}>Lowest Rating</button>
          <button onClick={() => sortGames('original')} className={sortOrder === 'original' ? 'btn-active' : ''}>Newest</button>

          <select onChange={(e) => filterByRatingRange(e.target.value)} value={ratingFilter}>
            <option value="">Filter by Rating</option>
            {[...Array(10)].map((_, i) => (
              <option key={i} value={i + 1}>{i + 1}</option>
            ))}
          </select>

          <select onChange={(e) => filterByDateRange(e.target.value)} value={dateFilter}>
            <option value="">Filter by Date</option>
            <option value="7">Last Week</option>
            <option value="30">Last Month</option>
            <option value="180">Last 6 Months</option>
            <option value="365">Last Year</option>
          </select>

          <select onChange={(e) => filterByPerson(e.target.value)} value={watchedWithFilter}>
            <option value="">Filter by person</option>
            {personOptions.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="movies-container">
        {games.map((game, idx) => (
          <div
            className="movie-card"
            key={idx}
            ref={(el) => (cardRefs.current[idx] = el)}
          >
            {game.poster_url && (
              <div className="movie-poster-wrap">
                <img
                  src={game.poster_url}
                  alt={`${game.title} Poster`}
                  className="movie-poster"
                  loading="lazy"
                  onDoubleClick={user ? () => downloadCardImage(cardRefs.current[idx], game.title) : undefined}
                  onTouchStart={user ? () => downloadLongPress.start(() => downloadCardImage(cardRefs.current[idx], game.title)) : undefined}
                  onTouchEnd={downloadLongPress.cancel}
                  onTouchCancel={downloadLongPress.cancel}
                  crossOrigin="anonymous"
                />
              </div>
            )}

            <div className="movie-content">
              <div className="movie-header">
                <h2>{game.title}</h2>
                <span
                  className="movie-rating"
                  onDoubleClick={user ? () => openEdit(game) : undefined}
                  onTouchStart={user ? () => editLongPress.start(() => openEdit(game)) : undefined}
                  onTouchEnd={editLongPress.cancel}
                  onTouchCancel={editLongPress.cancel}
                >{Number(game.rating).toFixed(1)}</span>
              </div>
              <div className="movie-thoughts">
                {game.thoughts || '*'}
              </div>
            </div>
          </div>
        ))}
      </div>

      {games.length === 0 && (
        <p className="no-results">Geen ene tering hier man.</p>
      )}

      {showAddForm && (
        <Modal onClose={() => setShowAddForm(false)}>
          <GamesForm onClose={() => { setShowAddForm(false); fetchGames(); }} />
        </Modal>
      )}

      {editingGame && (
        <Modal onClose={() => setEditingGame(null)}>
          <form onSubmit={handleEditSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <h2 style={{ fontFamily: 'angelWish', fontSize: '2.5rem', letterSpacing: '0.06em', margin: '0 0 1rem 0' }}>Edit Game</h2>
            <div className="form-group"><label>Title *</label><input type="text" name="title" value={editForm.title} onChange={handleEditChange} required /></div>
            <div className="form-group"><label>Rating *</label><input type="number" name="rating" value={editForm.rating} onChange={handleEditChange} min="1" max="10" step="0.1" required /></div>
            <div className="form-group"><label>Thoughts</label><textarea name="thoughts" value={editForm.thoughts} onChange={handleEditChange} rows={4} /></div>
            <div className="form-group">
              <label>Poster</label>
              {editingGame?.poster_url && !editPoster && (
                <img src={editingGame.poster_url} alt="poster" style={{ width: 80, borderRadius: 4, marginBottom: '0.4rem' }} />
              )}
              <input type="file" accept="image/*" onChange={(e) => setEditPoster(e.target.files[0])} />
            </div>
            <div className="form-group">
              <label>With</label>
              <div className="form-checkboxes">
                <label><input type="checkbox" name="playedWithJoy" checked={editForm.playedWithJoy} onChange={handleEditChange} /> Joy &lt;3</label>
                <label><input type="checkbox" name="playedWithRick" checked={editForm.playedWithRick} onChange={handleEditChange} /> Rick</label>
                <label><input type="checkbox" name="playedWithLisa" checked={editForm.playedWithLisa} onChange={handleEditChange} /> Lisa</label>
                <label><input type="checkbox" name="playedWithGionnan" checked={editForm.playedWithGionnan} onChange={handleEditChange} /> Gionnan</label>
                <label><input type="checkbox" name="playedWithYoussra" checked={editForm.playedWithYoussra} onChange={handleEditChange} /> Youssra</label>
              </div>
            </div>
            <div className="edit-modal-actions">
              <button type="button" className="form-submit" style={{ background: '#eee', color: '#333', borderColor: '#999', WebkitTextFillColor: '#333' }} onClick={() => setEditingGame(null)}>Cancel</button>
              <button type="button" className="form-submit btn-delete" onClick={handleDelete}>Delete</button>
              <button type="submit" className="form-submit" disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</button>
            </div>
            {editStatus && <p className="form-status">{editStatus}</p>}
          </form>
        </Modal>
      )}
    </div>
  );
}

export default App;
